/**
 * imageTransform.ts — the SINGLE serving/sizing seam for in-app photo display.
 *
 * A-WP1 (Photos v1) + the operator's Cloudflare-image-transformations amendment
 * (2026-07-10). Two things live here, and ONLY here, so the zone config can land
 * independently of this UI:
 *
 *   1. `variantForContext` — map a render context (strip thumb / card / full
 *      viewer) to the right stored derivative variant. In-app display fetches
 *      that variant through the session-minted `mintGetUrl` token path
 *      (`GET /media/:uuid/:variant?token=`), which is the shipped, access-checked
 *      serving route.
 *
 *   2. `withImageTransform` — wrap a download URL in a Cloudflare Images
 *      transformation (`/cdn-cgi/image/<params>/<url>`) for right-sizing +
 *      AVIF/WebP auto-negotiation. Per the amendment, transformations become the
 *      serving path once the zone is configured; until then this helper passes
 *      the URL through UNCHANGED (guarded by `enabled`, default off). Keeping the
 *      transform behind this one function means flipping the zone on is a
 *      one-line config change with no component edits.
 *
 * Refs:
 *   .plan/implementation/2026-07-10-track-a-record-build-plan.md A-WP1 +
 *     "Amendment — Cloudflare image transformations (operator, 2026-07-10)"
 *   architecture.md §4.8 — HMAC download-token serving route
 */

import type { MediaVariant } from "@proof/client-core/api/media";

export type RenderContext = "strip" | "card" | "full";

/**
 * The stored derivative variant for a render context.
 *   strip → thumb-256 (horizontal micro-strip / library card thumb)
 *   card  → card-768  (medium surfaces)
 *   full  → full-1600 (full-screen viewer — the largest stored copy, A6)
 */
export function variantForContext(ctx: RenderContext): MediaVariant {
  switch (ctx) {
    case "strip":
      return "thumb-256";
    case "card":
      return "card-768";
    case "full":
      return "full-1600";
  }
}

/**
 * Build the token-authenticated download URL for a media derivative.
 *
 * This is the shipped serving route (`mint-get-url.ts`): the caller mints a
 * short-lived HMAC token via `media.mintGetUrl`, then this URL streams the R2
 * derivative. Same-origin relative path — the session cookie + token gate access.
 */
export function mediaDownloadUrl(mediaUuid: string, variant: MediaVariant, token: string): string {
  return `/media/${encodeURIComponent(mediaUuid)}/${variant}?token=${encodeURIComponent(token)}`;
}

export interface ImageTransformOptions {
  /** Target display width in CSS px (the transform picks the DPR-scaled source). */
  width?: number;
  /** `scale-down` never upscales; the default for photo thumbs. */
  fit?: "scale-down" | "cover" | "contain";
  /**
   * Turn the Cloudflare Images transform ON. Default `false` until the zone is
   * configured (amendment consolidation follow-up). While off, the download URL
   * passes through unchanged and the pre-generated variant is served directly.
   */
  enabled?: boolean;
}

/**
 * Apply a Cloudflare Images transformation to a same-origin image URL.
 *
 * Shape: `/cdn-cgi/image/<comma-params>/<sourcePath>` (zone-level transform on
 * proofcook.com). Metadata is stripped on output and AVIF/WebP is auto-negotiated
 * by `format=auto` for free. When `enabled` is false (today's default) the source
 * URL is returned unchanged — so the pre-generated derivative variant is served
 * until the zone lands, with zero component churn at cut-over.
 */
export function withImageTransform(sourceUrl: string, opts: ImageTransformOptions = {}): string {
  if (!opts.enabled) return sourceUrl;
  const params = [
    "format=auto",
    `fit=${opts.fit ?? "scale-down"}`,
    opts.width ? `width=${Math.round(opts.width)}` : null,
  ]
    .filter((p): p is string => p !== null)
    .join(",");
  // Cloudflare expects the source as a path (leading slash preserved) after the
  // params segment. `sourceUrl` is already a same-origin relative path.
  const source = sourceUrl.startsWith("/") ? sourceUrl : `/${sourceUrl}`;
  return `/cdn-cgi/image/${params}${source}`;
}
